import AlphabetFilter from "@src/app/components/AlphabetFilter";
import type { IInfiniteScollListRef } from "@src/app/components/ArticleList/InfiniteScollList";
import InfiniteScollList from "@src/app/components/ArticleList/InfiniteScollList";
import { LoadingMore } from "@src/app/components/ArticleList/PageArticleList";
import ContributorCard from "@src/app/components/ContributorCard/ContributorCard";
import SectionContainer from "@src/app/components/SectionContainer/SectionContainer";
import type { BFFAuthorDetail, BFFResponseType } from "@src/app/types/bff";
import type { AuthorsPageResponse } from "@src/app/types/response";
import { createMetaTags } from "@src/app/utils/createMetaTags";
import { useEffect, useRef, useState } from "react";

export const meta = () =>
  createMetaTags({
    title: "Contributors | Big Reads, Opinions & Columns on China | ThinkChina",
    description:
      "ThinkChina Contributors - Find information about the thought leaders and experts who contribute to ThinkChina's diverse topics. ",
    canonical: `https://www.thinkchina.sg/contributors`,
    ogInfo: {
      ogTitle: "Contributors | Big Reads, Opinions & Columns on China ",
    },
  });

export default function ContributorsPage() {
  const [initialData, setInitialData] = useState<BFFAuthorDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [alphabet, setAlphabet] = useState("ALL");

  const ref = useRef<IInfiniteScollListRef>(null);

  const getInfo = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `/_plat/api/v2/client/contributors?alphabet=${alphabet}`,
      );
      const data = (await res.json()) as BFFResponseType<AuthorsPageResponse>;

      setInitialData(data.response.authors || []);
    } catch (error) {
      console.error("Error fetching contributors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alphabet]);

  const requestPath = `/contributors`;
  const requestQuery = { alphabet };

  return (
    <SectionContainer
      title="Our Contributors"
      className="mx-auto max-w-[1020px]"
    >
      <AlphabetFilter
        onSelect={(a) => {
          console.log("Selected alphabet:", a);
          setAlphabet(a?.toUpperCase() || "ALL");
        }}
      />
      {loading ? (
        <LoadingMore loading={loading} />
      ) : (
        <InfiniteScollList<BFFAuthorDetail>
          ref={ref}
          initialData={initialData}
          renderLoadingMore={(loading, hasMore) => (
            <LoadingMore loading={loading} hasMore={hasMore} />
          )}
          pageSize={12}
          fetchData={async ({ page, pageSize }) => {
            // when the article card is loaded or user scroll to the next section
            const params = { page, pageSize };
            const queryParams = Object.entries(requestQuery)
              .map(([key, value]) => `${key}=${value}`)
              .join("&");

            const paramStr = `?page=${params.page}&pageSize=${params.pageSize}&${queryParams}`;
            const res = await fetch(
              `/_plat/api/v2/client${requestPath}${paramStr}`,
            );
            console.log("Fetch contributors response:", res);

            const data =
              (await res.json()) as BFFResponseType<AuthorsPageResponse>;
            return data.response.authors || [];
          }}
          className="grid grid-cols-12"
          renderItem={(author, index) => {
            return (
              <div className="col-span-12 px-3 pb-4 lg:col-span-6">
                <ContributorCard author={author} key={author.id + index} />
              </div>
            );
          }}
        />
      )}
    </SectionContainer>
  );
}
